import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Close, InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetMapitShipOrdersByIdsQuery,
  useLazyGetWalletBalanceQuery,
} from "src/_api/mapitShipApi";
import { openConfirmDialog } from "src/_store/entities/confirmationDialog";
import { updateProfile } from "src/_store/entities/profile";
import { useSocket } from "src/_contexts/socket";
import ChargeBalanceDialog from "src/_modules/MapitShip/components/ChargeBalanceDialog";
import { ROLES } from "src/constants";
import DialogCODShipment from "../DeliveryCompanyListItem/DialogCODShipment";
import BulkCarrierPicker from "./BulkCarrierPicker";
import BulkRunTable from "./BulkRunTable";
import BulkResultsStep from "./BulkResultsStep";
import useBulkAssign from "./useBulkAssign";
import {
  BULK_CONTROL,
  BULK_PHASE,
  BULK_SETTLED_STATUSES,
  BULK_STATUS,
  needsCarrier,
} from "./constants";

const STEPS = [
  BULK_PHASE.carrier,
  BULK_PHASE.checking,
  BULK_PHASE.assigning,
  BULK_PHASE.done,
];

const STEP_LABEL_KEYS = {
  [BULK_PHASE.carrier]: "bulkAssign.step.carrier",
  [BULK_PHASE.checking]: "bulkAssign.step.check",
  [BULK_PHASE.assigning]: "bulkAssign.step.assign",
  [BULK_PHASE.done]: "bulkAssign.step.results",
};

const toCsv = (rows, companyName) => {
  const header = ["Order number", "Status", "Carrier", "Price", "Reason"];
  const body = rows.map((row) =>
    [
      row.orderNumber ?? "",
      row.status,
      row.status === BULK_STATUS.assigned ? companyName : "",
      Number.isFinite(row.price) ? row.price : "",
      (row.errorMsg ?? "").replace(/"/g, '""'),
    ]
      .map((cell) => `"${cell}"`)
      .join(","),
  );
  return [header.join(","), ...body].join("\n");
};

/**
 * Assigns ONE shipping company to many orders.
 *
 * The backend has no native bulk assign, so the run is two client-side passes
 * (availability, then assignment) over the same per-order endpoints the
 * one-by-one flow uses — see `useBulkAssign`. The wizard owns everything the
 * user sees plus the two gates that sit between the passes: the COD notice and
 * the wallet balance check.
 */
export default function BulkAssignWizard({
  open,
  orderIds,
  orders: ordersProp,
  onClose,
  onFinished,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDownMdScreen = useMediaQuery((th) => th.breakpoints.down("md"));
  const { profile } = useSelector((state) => state.entities.profile);
  const { credits } = useSelector(
    (state) => state.entities.creditsAndSubscription,
  );
  const { socket } = useSocket();

  const {
    rows,
    counts,
    phase,
    control,
    progress,
    assignedCost,
    prepare,
    runCheck,
    runAssign,
    retryFailed,
    pause,
    resume,
    stop,
    wasStopped,
    finishRun,
    reconcileRemaining,
    reset,
  } = useBulkAssign();

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [serviceType, setServiceType] = useState("");
  const [roundIds, setRoundIds] = useState(orderIds ?? []);
  const [codConfirmed, setCodConfirmed] = useState(false);
  const [openCod, setOpenCod] = useState(false);
  const [openBalance, setOpenBalance] = useState(false);
  const [shortfall, setShortfall] = useState(null);
  const [starting, setStarting] = useState(false);
  // Set when Start was interrupted by the COD notice, so confirming it resumes
  // the run instead of making the user press Start again.
  const [pendingStart, setPendingStart] = useState(false);

  // Every order assigned since the dialog was opened, across "assign the
  // remaining" rounds. `rows` only knows the current round — it is wiped by
  // `reset()` — but the page behind must be told about all of them no matter
  // which step the dialog is closed from.
  const assignedSoFarRef = useRef(new Set());
  // Set when the user closes mid-run: the run is stopped and the dialog closes
  // once the in-flight requests have settled, so nothing assigned is missed.
  const closeWhenSettledRef = useRef(false);

  // Only fetch when the caller did not already have the orders in hand.
  const shouldFetch = open && !ordersProp?.length && !!orderIds?.length;
  const { data: fetchedOrders, isFetching: ordersFetching } =
    useGetMapitShipOrdersByIdsQuery(
      { ids: orderIds ?? [] },
      { skip: !shouldFetch },
    );

  const allOrders = ordersProp?.length ? ordersProp : fetchedOrders;

  const roundOrders = useMemo(
    () => (allOrders ?? []).filter((order) => roundIds.includes(order.id)),
    [allOrders, roundIds],
  );

  const [getWalletBalance, { data: wallet }] = useLazyGetWalletBalanceQuery({});

  const isRunning =
    phase === BULK_PHASE.checking || phase === BULK_PHASE.assigning;
  // While the wallet gate holds the run, nothing is in flight: closing then
  // must not wait for a settle that will never come, and leaving the page is
  // safe.
  const runActive = isRunning && !shortfall;
  const isRunningRef = useRef(false);
  isRunningRef.current = runActive;

  const companyName = selectedCompany?.deliveryOptionName ?? "";

  // Reset the whole wizard whenever it is re-opened.
  useEffect(() => {
    if (open) {
      setRoundIds(orderIds ?? []);
      setSelectedCompany(null);
      setServiceType("");
      setCodConfirmed(false);
      setPendingStart(false);
      setShortfall(null);
      assignedSoFarRef.current = new Set();
      closeWhenSettledRef.current = false;
      reset();
      getWalletBalance({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A run in progress must survive neither a reload nor an accidental tab close.
  useEffect(() => {
    const handler = (event) => {
      if (!isRunningRef.current) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const onBalanceAdded = () => {
      getWalletBalance({});
      setOpenBalance(false);
    };
    socket.on("balanceAdded", onBalanceAdded);
    return () => socket.off("balanceAdded", onBalanceAdded);
  }, [socket, getWalletBalance]);

  /** Same three-tier resolution the single-order confirm dialog uses. */
  const resolveWalletBalance = useCallback(async () => {
    try {
      const fresh = await getWalletBalance({}).unwrap();
      if (Number.isFinite(Number(fresh?.balance))) return Number(fresh.balance);
    } catch (error) {
      console.error(error);
    }
    if (Number.isFinite(Number(wallet?.balance))) return Number(wallet.balance);
    if (Number.isFinite(Number(credits))) return Number(credits);
    return null;
  }, [credits, getWalletBalance, wallet]);

  const markCodShipmentSeen = useCallback(() => {
    if (profile?.merchant?.hasCodShipment === false) {
      dispatch(
        updateProfile({
          ...profile,
          merchant: { ...profile.merchant, hasCodShipment: true },
        }),
      );
    }
  }, [dispatch, profile]);

  const needsCodNotice =
    profile?.merchant?.role === ROLES.merchant &&
    profile?.merchant?.hasCodShipment === false &&
    roundOrders.some((order) => Number(order?.amountPayable) > 0);

  const recordAssigned = useCallback(
    (result) => {
      // Settled = assigned by this run + found to already have a carrier. The
      // page behind hides both; only the former flips the COD flag.
      (result?.settledIds ?? result?.assignedIds ?? []).forEach((id) =>
        assignedSoFarRef.current.add(id),
      );
      if (result?.assignedIds?.length) markCodShipmentSeen();
      return result;
    },
    [markCodShipmentSeen],
  );

  /** Runs the assignment pass, then hands the outcome back to the caller. */
  const assignEligible = useCallback(async () => {
    setShortfall(null);
    return recordAssigned(await runAssign());
  }, [recordAssigned, runAssign]);

  const handleRetryFailed = useCallback(async () => {
    recordAssigned(await retryFailed());
  }, [recordAssigned, retryFailed]);

  const handleStart = useCallback(async () => {
    if (!selectedCompany || !roundOrders.length) return;

    if (needsCodNotice && !codConfirmed) {
      setOpenCod(true);
      return;
    }

    setStarting(true);
    try {
      prepare(roundOrders);
      roundOrders
        .filter((order) => !needsCarrier(order))
        .forEach((order) => assignedSoFarRef.current.add(order.id));

      const eligible = await runCheck({
        orders: roundOrders,
        company: selectedCompany,
        serviceType,
      });

      // Nothing left to do — either the carrier fits no order, or the user
      // stopped the scan. Either way go straight to the results screen, after
      // checking with the server that the leftovers really are leftovers.
      if (!eligible.length || wasStopped()) {
        await reconcileRemaining();
        finishRun();
        return;
      }

      const cost = eligible.reduce(
        (sum, row) => sum + (Number.isFinite(row.price) ? row.price : 0),
        0,
      );
      const balance = await resolveWalletBalance();

      // The user may have pressed Stop (or closed) while the wallet was being
      // read; the assign pass must not start behind their back.
      if (wasStopped()) {
        await reconcileRemaining();
        finishRun();
        return;
      }

      // Only block when the balance is known to be short. An unreadable balance
      // is left to the server, exactly as the single-order flow does.
      if (balance !== null && balance < cost) {
        setShortfall(cost - balance);
        setOpenBalance(true);
        return;
      }

      await assignEligible();
    } finally {
      setStarting(false);
    }
  }, [
    assignEligible,
    codConfirmed,
    finishRun,
    needsCodNotice,
    prepare,
    reconcileRemaining,
    resolveWalletBalance,
    roundOrders,
    runCheck,
    selectedCompany,
    serviceType,
    wasStopped,
  ]);

  useEffect(() => {
    if (pendingStart && codConfirmed) {
      setPendingStart(false);
      handleStart();
    }
  }, [codConfirmed, handleStart, pendingStart]);

  const remainingIds = useMemo(() => {
    if (phase === BULK_PHASE.done) {
      return rows
        .filter((row) => !BULK_SETTLED_STATUSES.includes(row.status))
        .map((row) => row.orderId);
    }
    // Before this round has run (e.g. step 1 of an "assign the remaining"
    // round), everything carried into it is still pending.
    return roundIds.filter((id) => !assignedSoFarRef.current.has(id));
  }, [phase, roundIds, rows]);

  /**
   * The single exit. If anything was assigned since the dialog opened, the
   * caller gets the full picture through `onFinished`; otherwise it is a plain
   * close. Every button and the X go through here.
   */
  const closeAndReport = useCallback(() => {
    if (assignedSoFarRef.current.size) {
      onFinished?.(remainingIds, Array.from(assignedSoFarRef.current));
    } else {
      onClose?.();
    }
  }, [onClose, onFinished, remainingIds]);

  // Deferred close requested mid-run: fire once the run has settled.
  useEffect(() => {
    if (phase === BULK_PHASE.done && closeWhenSettledRef.current) {
      closeWhenSettledRef.current = false;
      closeAndReport();
    }
  }, [closeAndReport, phase]);

  const handleClose = useCallback(() => {
    if (runActive) {
      dispatch(
        openConfirmDialog({
          title: t("Warning"),
          content: t("bulkAssign.closeWarning"),
          onConfirm: () => {
            closeWhenSettledRef.current = true;
            stop();
          },
        }),
      );
      return;
    }
    closeAndReport();
  }, [closeAndReport, dispatch, runActive, stop, t]);

  const handleAssignRemaining = useCallback(() => {
    setRoundIds(remainingIds);
    setSelectedCompany(null);
    setCodConfirmed(true);
    setShortfall(null);
    reset();
  }, [remainingIds, reset]);

  const handleContinueOneByOne = useCallback(() => {
    const ids = remainingIds.join(",");
    closeAndReport();
    navigate(
      `/merchant/mapit-ship/shipments/price-calculator/list?ordersIds=${ids}`,
    );
  }, [closeAndReport, navigate, remainingIds]);

  const handleDownloadReport = useCallback(() => {
    const blob = new Blob([`﻿${toCsv(rows, companyName)}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk-assign-report.csv";
    link.click();
    URL.revokeObjectURL(url);
  }, [companyName, rows]);

  const activeStep = STEPS.indexOf(phase);

  const title =
    phase === BULK_PHASE.done
      ? t("bulkAssign.doneTitle", {
          assigned: counts.assigned,
          total: counts.total,
          company: companyName,
        })
      : t("bulkAssign.title", {
          company: companyName || t("A Shipping Company"),
          total: roundOrders.length,
        });

  const progressValue = progress.total
    ? (progress.done / progress.total) * 100
    : 0;

  return (
    <>
      <DialogCODShipment
        open={openCod}
        onConfirm={() => {
          setOpenCod(false);
          setCodConfirmed(true);
          setPendingStart(true);
        }}
      />
      <ChargeBalanceDialog
        open={openBalance}
        onClose={() => setOpenBalance(false)}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        dir={theme.direction}
        fullWidth
        maxWidth="lg"
        fullScreen={isDownMdScreen}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {title}
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ my: 2 }} alternativeLabel>
            {STEPS.map((step) => (
              <Step key={step}>
                <StepLabel>{t(STEP_LABEL_KEYS[step])}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {phase === BULK_PHASE.carrier && (
            <>
              {ordersFetching ? (
                <Box>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Skeleton key={index} variant="text" height={40} />
                  ))}
                </Box>
              ) : (
                <BulkCarrierPicker
                  selectedCompany={selectedCompany}
                  onSelectCompany={setSelectedCompany}
                  serviceType={serviceType}
                  setServiceType={setServiceType}
                />
              )}
            </>
          )}

          {(phase === BULK_PHASE.checking || phase === BULK_PHASE.assigning) && (
            <Box>
              {!shortfall && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {phase === BULK_PHASE.checking
                      ? t("bulkAssign.checkingProgress", {
                          done: progress.done,
                          total: progress.total,
                        })
                      : t("bulkAssign.assigningProgress", {
                          done: progress.done,
                          total: progress.total,
                        })}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progressValue}
                    sx={{ mb: 2, height: 8, borderRadius: 4 }}
                  />
                </>
              )}

              {!!shortfall && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t("bulkAssign.walletShortfall", {
                    amount: shortfall.toFixed(2),
                    total: counts.eligible,
                  })}
                </Alert>
              )}

              {control === BULK_CONTROL.paused && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("bulkAssign.pausedHint")}
                </Alert>
              )}

              <BulkRunTable
                rows={rows}
                counts={counts}
                companyName={companyName}
              />
            </Box>
          )}

          {phase === BULK_PHASE.done && (
            <BulkResultsStep
              rows={rows}
              counts={counts}
              assignedCost={assignedCost}
              companyName={companyName}
            />
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, p: 2 }}>
          {phase === BULK_PHASE.carrier && (
            <>
              <Box mr="auto" />
              <LoadingButton
                onClick={handleClose}
                variant="outlined"
                sx={{ textTransform: "none" }}
              >
                {t("Cancel")}
              </LoadingButton>
              <LoadingButton
                onClick={handleStart}
                loading={starting}
                disabled={!selectedCompany || !roundOrders.length}
                variant="contained"
                sx={{ textTransform: "none" }}
              >
                {t("bulkAssign.startRun", { total: roundOrders.length })}
              </LoadingButton>
            </>
          )}

          {isRunning && (
            <>
              <Box mr="auto" />
              {/* The wallet gate parks the run between the two passes. */}
              {!!shortfall ? (
                <>
                  <LoadingButton
                    onClick={assignEligible}
                    variant="outlined"
                    sx={{ textTransform: "none" }}
                  >
                    {t("Continue")}
                  </LoadingButton>
                  <LoadingButton
                    onClick={() => setOpenBalance(true)}
                    variant="contained"
                    sx={{ textTransform: "none" }}
                  >
                    {t("Charge Balance")}
                  </LoadingButton>
                </>
              ) : (
                <>
                  {/* Each control carries its own info icon: "pause" and "stop"
                      are easy to confuse, and the difference only matters at
                      the moment the user reaches for one of them. */}
                  <Box display="flex" alignItems="center">
                    <Tooltip
                      placement="top"
                      enterTouchDelay={0}
                      leaveTouchDelay={6000}
                      title={
                        control === BULK_CONTROL.paused
                          ? t("bulkAssign.resumeTooltip")
                          : t("bulkAssign.pauseTooltip")
                      }
                    >
                      <InfoOutlined
                        fontSize="small"
                        sx={{ mr: 0.5, color: "text.lighter", cursor: "help" }}
                      />
                    </Tooltip>
                    <LoadingButton
                      onClick={
                        control === BULK_CONTROL.paused ? resume : pause
                      }
                      variant="outlined"
                      sx={{ textTransform: "none" }}
                    >
                      {control === BULK_CONTROL.paused
                        ? t("Resume")
                        : t("Pause")}
                    </LoadingButton>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Tooltip
                      placement="top"
                      enterTouchDelay={0}
                      leaveTouchDelay={6000}
                      title={t("bulkAssign.stopTooltip")}
                    >
                      <InfoOutlined
                        fontSize="small"
                        sx={{ mr: 0.5, color: "text.lighter", cursor: "help" }}
                      />
                    </Tooltip>
                    <LoadingButton
                      onClick={stop}
                      color="error"
                      variant="outlined"
                      sx={{ textTransform: "none" }}
                    >
                      {t("Stop after current")}
                    </LoadingButton>
                  </Box>
                </>
              )}
            </>
          )}

          {phase === BULK_PHASE.done && (
            <>
              <LoadingButton
                onClick={handleDownloadReport}
                variant="text"
                sx={{ textTransform: "none" }}
              >
                {t("Download report")}
              </LoadingButton>
              <Box mr="auto" />
              {counts.failed > 0 && (
                <LoadingButton
                  onClick={handleRetryFailed}
                  variant="outlined"
                  sx={{ textTransform: "none" }}
                >
                  {t("Retry all")}
                </LoadingButton>
              )}
              {remainingIds.length > 0 && (
                <>
                  <LoadingButton
                    onClick={handleContinueOneByOne}
                    variant="outlined"
                    sx={{ textTransform: "none" }}
                  >
                    {t("bulkAssign.continueOneByOne")}
                  </LoadingButton>
                  <LoadingButton
                    onClick={handleAssignRemaining}
                    variant="contained"
                    sx={{ textTransform: "none" }}
                  >
                    {t("bulkAssign.assignRemaining", {
                      total: remainingIds.length,
                    })}
                  </LoadingButton>
                </>
              )}
              <LoadingButton
                onClick={closeAndReport}
                variant={remainingIds.length ? "text" : "contained"}
                sx={{ textTransform: "none" }}
              >
                {t("Back to orders")}
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
